import { Component, OnInit, QueryList, SecurityContext, ViewChild, ViewChildren } from '@angular/core';
import { ActiveRecordsApiService } from 'src/app/services/api/active-records-api.service';
import { LocaleService } from 'src/app/services/locale.service';
import { UserService } from 'src/app/services/user.service';
import { BaseData } from '../card-item/models/base-data.model';
import { DetailsCardItem } from '../card-item/models/details-card-item.model';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { DominantHexColors } from '../card-item/models/dominant-colors.model';
import { Router } from '@angular/router';
import { Record } from 'src/app/models/record';
import { Util } from 'src/app/util';
import { DomSanitizer } from '@angular/platform-browser';
import moment from 'moment';
import { ConfigService } from 'src/app/services/config.service';
import { CustomerType } from 'src/app/models/client-configuration';
import { SearchModel } from 'src/app/models/search-model';
import {
  ActiveRecord,
  ActiveRecordsResponse,
} from 'src/app/services/api/models/active-records/active-records.response';
import { CheckoutsFacade } from 'src/app/state/user/checkouts/checkouts.facade';
import { SwiperOptions } from 'swiper/types/swiper-options';
import { SwiperComponent } from '../../_shared/components/swiper/swiper.component';
import { CardItemComponent } from '../card-item/card-item.component';
import ColorThief from 'colorthief';

@Component({
  selector: 'active-records-swiper',
  templateUrl: './active-records-swiper.component.html',
  styleUrls: ['./active-records-swiper.component.scss'],
})
export class ActiveRecordsSwiperComponent implements OnInit {
  private readonly BUTTON_BASE_OFFSET_PX = 8;
  private readonly colorThief = new ColorThief();

  readonly swiperConfig: SwiperOptions = {
    freeMode: true,
    watchOverflow: true,
    spaceBetween: 16,
    breakpoints: {
      0: { slidesPerView: 1.08, slidesPerGroup: 1 },
      768: { slidesPerView: 1.4, slidesPerGroup: 1 },
      992: { slidesPerView: 1.8, slidesPerGroup: 1 },
      1200: { slidesPerView: 2.15, slidesPerGroup: 2 },
      1600: { slidesPerView: 3.15, slidesPerGroup: 3 },
      1800: { slidesPerView: 3.15, slidesPerGroup: 3 },
    },
    slidesOffsetAfter: 8, //selectedOffset,
    slidesOffsetBefore: 8, //selectedOffset,
    navigation: {
      enabled: !this.configService.notDesktopDevice(),
    },
  };

  @ViewChild(SwiperComponent) swiper: SwiperComponent;
  @ViewChildren(CardItemComponent) cards: QueryList<CardItemComponent>;

  customerType: CustomerType;

  userServiceInfo: UserService;
  textDirection: string;
  buttonsOffset: string;
  buttonsOffsetPx = 0;

  activeRecords: ActiveRecord[] = [];
  canMakeCheckouts: boolean = false;
  dominantColors: DominantHexColors;

  loaded = false;

  constructor(
    private localeService: LocaleService,
    private datePipe: DatePipe,
    private translate: TranslateService,
    private activeRecordsApiService: ActiveRecordsApiService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private configService: ConfigService,
    private checkoutsFacade: CheckoutsFacade
  ) {}

  ngOnInit(): void {
    this.customerType = this.configService.customerType;
    this.getActiveRecords();
    this.textDirection = this.localeService.getTextDirection();
    this.buttonsOffset = `${this.BUTTON_BASE_OFFSET_PX - this.buttonsOffsetPx}px`;
  }

  ngAfterViewInit() {
    this.cards.changes.subscribe((queryList) => {
      if (queryList.length === this.activeRecords.length) {
        this.swiper.swiperContainer.nativeElement.swiper.updateSlides();
      }
    });
  }

  goToCheckouts() {
    this.router.navigate(['/user/checkouts']);
  }

  exploreFullCatalog() {
    const model = new SearchModel();
    model.explore = true;
    this.router.navigate(['/results'], {
      queryParams: model.toRequest(),
    });
  }

  navToInfo(activeRecord: ActiveRecord) {
    const slug = Util.slugifyRecord(activeRecord.record);
    this.router.navigate(['/info', slug]);
  }

  private getActiveRecords() {
    this.activeRecordsApiService.getActiveRecords().subscribe({
      next: async (activeRecordsResponse: ActiveRecordsResponse) => {
        this.canMakeCheckouts = activeRecordsResponse.canMakeCheckouts;
        for (const activeRecord of activeRecordsResponse.activeRecords) {
          await this.getColorFromImage(activeRecord.coverUrl);

          activeRecord.record = {
            ...Util.prepareRecord(activeRecord.record),
          };
          const baseData = this.buildBaseData(activeRecord, activeRecord.record);
          this.activeRecords.push({
            ...activeRecord,
            baseData: baseData,
            detailsLeft: this.fillLeftDetails(activeRecord),
            dominantColors: this.dominantColors,
            issueDate: Util.getLastIssueDate(activeRecord.record),
          });
        }
        if (!this.diffCheckouts(activeRecordsResponse.activeRecords)) {
          this.checkoutsFacade.getCheckouts();
        }
        this.loaded = true;
      },
      error: (error) => {
        this.loaded = false;
        return console.error(error);
      },
    });
  }

  private diffCheckouts(activeRecords: ActiveRecord[]): boolean {
    const activeCheckouts = activeRecords.filter((activeRecord) => activeRecord.checkoutId);
    const checkoutFacadeRecords = this.checkoutsFacade.checkouts.map((checkout) => checkout.recordId);
    return activeCheckouts.every((record) => checkoutFacadeRecords.includes(record.recordId));
  }

  private buildBaseData(activeRecord: ActiveRecord, record: Record): BaseData {
    return {
      record: record,
      author: activeRecord.author,
      title: activeRecord.title,
      cover: activeRecord.coverUrl,
      icon: Util.resolveIcon(record, record.iconId),
      fromBookClub: record.fromBookClub,
    };
  }

  private fillLeftDetails(activeRecord: ActiveRecord) {
    const leftDetails: DetailsCardItem[] = [];
    if ((activeRecord.checkoutId && activeRecord.timeLeft) || (!activeRecord.checkoutId && activeRecord.endDate)) {
      const label = activeRecord.checkoutId
        ? this.translate.instant('user.checkouts.left')
        : this.translate.instant('user.checkouts.expires');
      const time = activeRecord.checkoutId
        ? this.calculateExpiringTime(activeRecord.timeLeft)
        : this.datePipe.transform(activeRecord.endDate);
      leftDetails.push({
        label: label,
        data: time,
      });
    }
    return leftDetails;
  }

  private calculateExpiringTime(timeLeft: number): string {
    const timeLeftMoment = moment(timeLeft);
    const diff = moment.duration(moment(timeLeftMoment).diff(moment()));

    const timeLeftValue = this.localeService.humanize(diff.asMilliseconds());
    return String(timeLeftValue.valueOf());
  }

  private async getColorFromImage(imageUrl: string) {
    const imageSanitizedUrl = imageUrl ? this.sanitizer.sanitize(SecurityContext.URL, imageUrl) : 'assets/cover.jpg';
    let image = null;

    try {
      image = await this.loadImage(imageSanitizedUrl);
    } catch {
      image = await this.loadImage('assets/cover.jpg');
    } finally {
      if (image !== null) {
        this.extractColors(image);
      }
    }
  }

  private loadImage(imageSanitizedUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'Anonymous';
      image.src = imageSanitizedUrl;
      image.onload = () => resolve(image);
      image.onerror = () => reject();
    });
  }

  private extractColors(image: HTMLImageElement): void {
    const dominantColor = this.colorThief.getColor(image);
    const fullPalette = this.colorThief.getPalette(image);

    this.dominantColors = {
      showGradient: true,
      dominant: dominantColor,
      secondary: fullPalette[0],
    };
  }
}
